/**
 * Reservation Management Module - Query Services
 *
 * GET hooks for TaskManagement endpoints.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  TASK_MANAGEMENT_URL,
  TASK_MANAGEMENT_STATS_URL,
  RESERVATION_LIST_URL,
  RESERVATION_DETAIL_URL,
  AVAILABLE_PROPERTIES_URL,
} from '../constants';
import type {
  IApiResponse,
  IStatsResult,
  IReservationListResult,
  IReservationListParams,
  ITaskManagementDashboardResult,
  IReservationDetail,
  IAvailableProperty,
  IAvailablePropertiesParams,
} from '../types';

/**
 * Fetches the full task management dashboard with status categories.
 * GET /api/TaskManagement
 */
export const useTaskManagementDashboard = () => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<ITaskManagementDashboardResult>>({
    queryKey: [TASK_MANAGEMENT_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get<IApiResponse<ITaskManagementDashboardResult>>(
        TASK_MANAGEMENT_URL
      );
      return data;
    },
  });
};

/**
 * Fetches stats cards data (check-in today, check-out today, upcoming, vacant).
 * GET /api/TaskManagement/Stats
 */
export const useTaskManagementStats = () => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IStatsResult>>({
    queryKey: [TASK_MANAGEMENT_STATS_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get<IApiResponse<IStatsResult>>(
        TASK_MANAGEMENT_STATS_URL
      );
      return data;
    },
  });
};

/**
 * Fetches paginated reservation list with filters (tab, search, status, price, etc.).
 * GET /api/TaskManagement/ReservationList
 */
export const useReservationList = (params: IReservationListParams) => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IReservationListResult>>({
    queryKey: [RESERVATION_LIST_URL, params],
    queryFn: async () => {
      const { data } = await apiPrivate.get<IApiResponse<IReservationListResult>>(
        RESERVATION_LIST_URL,
        { params }
      );
      return data;
    },
  });
};

/**
 * Fetches a single reservation detail.
 * GET /api/TaskManagement/Reservation/{reservationId}
 */
export const useReservationDetail = (reservationId?: number) => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IReservationDetail>>({
    queryKey: [RESERVATION_DETAIL_URL, reservationId],
    queryFn: async () => {
      const { data } = await apiPrivate.get<IApiResponse<IReservationDetail>>(
        `${RESERVATION_DETAIL_URL}/${reservationId}`
      );
      return data;
    },
    enabled: !!reservationId,
  });
};

/**
 * Fetches available properties for an extension.
 * GET /api/TaskManagement/Extension/AvailableProperties
 */
export const useAvailableProperties = (params?: IAvailablePropertiesParams) => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IAvailableProperty[]>>({
    queryKey: [AVAILABLE_PROPERTIES_URL, params?.reservationId, params?.extensionCheckoutDate],
    queryFn: async () => {
      const { data } = await apiPrivate.get<IApiResponse<IAvailableProperty[]>>(
        AVAILABLE_PROPERTIES_URL,
        { params }
      );
      return data;
    },
    enabled: !!params?.reservationId,
  });
};
